import { create } from 'zustand';
import { SiteSettings } from '../types';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@services/firebase';

interface SettingsState {
    settings: SiteSettings | null;
    loading: boolean;
    error: string | null;
    setSettings: (settings: SiteSettings) => void;
    updateSettings: (newSettings: Partial<SiteSettings>, userId: string) => Promise<void>;
    fetchSettings: () => any;
}

// Valeurs par défaut ultra-premium pour le premier lancement
const defaultSettings: SiteSettings = {
    maxUploadSizeMB: 5,
    minTradesForHub: 10,
    maxCharsStrategyTitle: 50,
    maxCharsStrategyDescription: 1000,
    maxCharsEntryRules: 2000,
    maxCharsExitRules: 2000,
    maxCharsSlPlacement: 500,
    maxCharsTradeManagement: 1000,
    maxCharsTradeNotes: 2000,
    maxFundamentalAnalysisPerDay: 5,
    minTradesForAudit: 10,
    maxTradesPerAudit: 50,
    maxAuditsPerDay: 3,
    maxContactMessagesPerDay: 3,
    paginationDashboard: 5,
    paginationHub: 12,
    paginationAdminContent: 5,
    paginationAdminUsers: 5,
    paginationTrades: 10,
    paginationBlog: 6,
    paginationNotifications: 5,
    paginationCampaignHistory: 10,
    paginationFAQ: 10,
    paginationContactMessages: 5,
    pricing: {
        advanced: {
            monthly: 29.99,
            yearly: 299.90
        },
        professional: {
            monthly: 49.99,
            yearly: 499.90
        },
        yearlyDiscountPercent: 17
    },
    // Maintenance
    maintenanceMode: false,
    maintenanceMessage: 'StarQuant est actuellement en maintenance pour une mise à jour majeure. Nous serons de retour très bientôt !',
    maintenanceDuration: '2 heures',
    updatedAt: new Date(),
    updatedBy: 'system'
};

export const useSettingsStore = create<SettingsState>((set) => ({
    settings: defaultSettings,
    loading: false,
    error: null,

    setSettings: (settings) => set({ settings, loading: false }),

    fetchSettings: () => {
        const settingsDoc = doc(db, 'config', 'site_settings');

        return onSnapshot(settingsDoc, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                // Conversion des timestamps Firestore en Date JS
                const cleanedData = {
                    ...data,
                    updatedAt: data.updatedAt?.toDate() || new Date()
                } as SiteSettings;
                set({ settings: cleanedData, loading: false });
            } else {
                // Si pas de réglages, on utilise les défauts
                set({ settings: defaultSettings, loading: false });
            }
        }, (err) => {
            console.error("Erreur SettingsStore:", err);
            set({ error: err.message, loading: false });
        });
    },

    updateSettings: async (newSettings, userId) => {
        try {
            const settingsDoc = doc(db, 'config', 'site_settings');
            const dataToUpdate = {
                ...newSettings,
                updatedAt: serverTimestamp(),
                updatedBy: userId
            };
            await setDoc(settingsDoc, dataToUpdate, { merge: true });
        } catch (err: any) {
            console.error("Erreur mise à jour réglages:", err);
            throw err;
        }
    }
}));
